import { Injectable }                 from '@angular/core';
import {
	Event,
	NavigationEnd,
	Router
}                                     from '@angular/router';
import { DialogModalCommonType }      from '@app/core/models/dialog-modal-common-type.enum';
import { ModalSizeEnum }              from '@core/modals/modal.model';
import {
	NgbModal,
	NgbModalOptions,
	NgbModalRef
}                                     from '@ng-bootstrap/ng-bootstrap';
import { LoggerService }              from '@shared/services/logger.service';
import {
	Observable,
	Subject
}                                     from 'rxjs';
import {
	filter,
	take
}                                     from 'rxjs/operators';
import { DialogModalCommonComponent } from './dialog-modal-common.component';


@Injectable()
export class DialogModalCommonService {

	constructor(
		protected readonly logger: LoggerService,
		protected readonly modalService: NgbModal,
		private readonly router: Router
	) { }

	private subscribeToRouterEvents( modalRef: NgbModalRef ): void {
		this.router.events
		.pipe(
			filter( ( routerEvent: Event ) => routerEvent instanceof NavigationEnd ),
			take( 1 )
		)
		.subscribe( () => {
			modalRef?.close();
		} );
	}

	showDialogCommon(
		dialogOptions: {
			cancelType?: string,
			cancelButtonBaseI18nPath?: string,
			confirmType?: string,
			confirmButtonBaseI18nPath?: string,
			description?: string,
			message?: any,
			option?: NgbModalOptions,
			title?: string,
			type: DialogModalCommonType
		}
	): NgbModalRef {

		dialogOptions.option = {
			...dialogOptions?.option,
			animation: dialogOptions?.option?.animation ?? true,
			centered: dialogOptions?.option?.centered ?? true,
			size: dialogOptions?.option?.size ?? ModalSizeEnum.LG,
			windowClass: dialogOptions?.option?.windowClass ? dialogOptions?.option.windowClass + ' overlay' : 'overlay',
			backdrop: dialogOptions?.option?.backdrop ?? true,
			backdropClass: dialogOptions?.option?.backdropClass ? dialogOptions?.option.backdropClass + ' overlay' : 'overlay'
		};


		const modalRef = this.modalService.open( DialogModalCommonComponent, dialogOptions?.option );

		modalRef.componentInstance.cancelButtonBaseI18nPath = dialogOptions?.cancelButtonBaseI18nPath ?? 'shared.buttons.';
		modalRef.componentInstance.cancelType = dialogOptions?.cancelType ?? 'cancel';
		modalRef.componentInstance.confirmType = dialogOptions?.confirmType ?? 'confirm';
		modalRef.componentInstance.confirmButtonBaseI18nPath = dialogOptions?.confirmButtonBaseI18nPath ?? 'shared.buttons.';
		modalRef.componentInstance.description = dialogOptions?.description;
		modalRef.componentInstance.message = dialogOptions?.message;
		modalRef.componentInstance.title = dialogOptions?.title;
		modalRef.componentInstance.type = dialogOptions?.type;

		this.subscribeToRouterEvents( modalRef );
		return modalRef;
	}

	showDialogCommonWithResultSubject(
		type: DialogModalCommonType,
		title?: string,
		description?: string,
		message?: any,
		confirmType?: string,
		option?: NgbModalOptions,
		cancelType?: string,
		cancelButtonBaseI18nPath?: string,
		confirmButtonBaseI18nPath?: string
	): Observable<string> {

		const modalRef = this.showDialogCommon( {
			cancelButtonBaseI18nPath,
			cancelType,
			confirmButtonBaseI18nPath,
			confirmType,
			description,
			message,
			option,
			title,
			type
		} );

		const resultSubject = new Subject<string>();
		modalRef.result
		.then( () => {
			resultSubject.next( confirmType );

			resultSubject.complete();
			resultSubject.unsubscribe();
		} );

		return resultSubject;
	}

	showConfirmationDialog(
		description: string,
		confirmType: string = 'confirm',
		replaceTextPlaceholders?: { [ key: string ]: any },
		message?: any,
		cancelType: string = 'cancel',
		cancelButtonBaseI18nPath?: string,
		confirmButtonBaseI18nPath?: string
	): Observable<string> {

		const option: NgbModalOptions = {
			centered: true,
			size: ModalSizeEnum.LG,
			windowClass: 'overlay',
			backdrop: true,
			backdropClass: 'overlay'
		};

		const modalRef = this.modalService.open( DialogModalCommonComponent, option );

		modalRef.componentInstance.cancelButtonBaseI18nPath = cancelButtonBaseI18nPath ?? 'shared.buttons.';
		modalRef.componentInstance.cancelType = cancelType;
		modalRef.componentInstance.confirmButtonBaseI18nPath = confirmButtonBaseI18nPath ?? 'shared.buttons.';
		modalRef.componentInstance.confirmType = confirmType;
		modalRef.componentInstance.description = description;
		modalRef.componentInstance.message = message;
		modalRef.componentInstance.replaceTextPlaceholders = replaceTextPlaceholders;
		modalRef.componentInstance.type = DialogModalCommonType.QUESTION;

		this.subscribeToRouterEvents( modalRef );

		const resultSubject = new Subject<string>();
		modalRef.result
		.then( res => {
			res === confirmType
			? resultSubject.next( confirmType )
			: this.logger.log( 'DialogCommonModal closed' );

			resultSubject.complete();
			resultSubject.unsubscribe();
		} );

		return resultSubject;
	}
}
